// src/components/UserRecipesList.jsx
import React from "react";
import PropTypes from "prop-types";
import { Card, CardBody } from "./../../components/card/card.jsx";
import defaultCocktail from "../../assets/images/default_cocktail.png";

// Helper function to extract comma-separated inputUserName from an array.
const getInputUserNameList = (arr) => {
  if (Array.isArray(arr) && arr.length > 0) {
    return arr.map((item) => item.inputUserName).filter(Boolean).join(", ");
  }
  return "";
};

// Component to render a single recipe row.
const RecipeRow = ({ recipe, rank, themeColor }) => {
  const imageUrl =
    recipe.image && recipe.image !== "none" ? recipe.image : defaultCocktail;

  // 1) Try the custom-array fields
  const customBase    = getInputUserNameList(recipe.custom_recipe_ingredient_base);
  const customMixer   = getInputUserNameList(recipe.custom_recipe_ingredient_mixer);
  const customGarnish = getInputUserNameList(recipe.custom_recipe_ingredient_garnish);

  // 2) Try the simple *List props
  const propBase    = recipe.baseList    || "";
  const propMixer   = recipe.mixerList   || "";
  const propGarnish = recipe.garnishList || "";

  // 3) Fallback: parse recipe.ingredients JSON for curated recipes
  let ingrBase = "", ingrMixer = "", ingrGarnish = "";
  if (recipe.ingredients) {
    try {
      const parsed = JSON.parse(recipe.ingredients);
      ingrBase    = parsed.ingredient_base?.name    || "";
      ingrMixer   = parsed.ingredient_mixer?.name   || "";
      ingrGarnish = parsed.ingredient_garnish?.name || "";
    } catch (e) {
      console.warn("Failed to parse recipe.ingredients:", e);
    }
  }

  // 4) Final values
  const baseList    = customBase    || propBase    || ingrBase    || "N/A";
  const mixerList   = customMixer   || propMixer   || ingrMixer   || "N/A";
  const garnishList = customGarnish || propGarnish || ingrGarnish || "N/A";

  // 5) Determine the “crafted by” or “type” label
  const craftedOrType = recipe.username
    ? `Crafted By: ${recipe.username}`
    : recipe.type
      ? `Type: ${recipe.type.charAt(0).toUpperCase() + recipe.type.slice(1)}`
      : "Unknown";

  return (
    <tr style={{ verticalAlign: "middle" }}>
      {/* Rank Column */}
      <td
        style={{
          width: "40px",
          textAlign: "center",
          fontWeight: "bold",
          color: themeColor,
          borderRight: `1px solid ${themeColor}`,
        }}
      >
        {rank}
      </td>
      {/* Image Column */}
      <td style={{ width: "80px", padding: "0 10px" }}>
        <img
          src={imageUrl}
          alt={recipe.name}
          style={{
            width: "70px",
            height: "70px",
            objectFit: "cover",
            borderRadius: "0.25rem",
            backgroundColor: "#fff"
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = defaultCocktail;
          }}
        />
      </td>
      {/* Details Column */}
      <td style={{ paddingLeft: "10px" }}>
        <h5 style={{ margin: "0 0 4px 0", fontSize: 15 }}>{recipe.name}</h5>
        <div>
          <small style={{ color: themeColor, fontSize: 14 }}>
            {craftedOrType}
          </small>
        </div>
        <div style={{ marginTop: "4px" }}>
          <div>
            <strong>Base:</strong> {baseList}
          </div>
          <div>
            <strong>Mixer:</strong> {mixerList}
          </div>
          <div>
            <strong>Garnish:</strong> {garnishList}
          </div>
        </div>
      </td>
    </tr>
  );
};

RecipeRow.propTypes = {
  recipe: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    username: PropTypes.string,
    type: PropTypes.string,            // added
    baseList: PropTypes.string,
    mixerList: PropTypes.string,
    garnishList: PropTypes.string,
    ingredients: PropTypes.string,
    custom_recipe_ingredient_base: PropTypes.array,
    custom_recipe_ingredient_garnish: PropTypes.array,
    custom_recipe_ingredient_mixer: PropTypes.array,
    image: PropTypes.string,
  }).isRequired,
  rank: PropTypes.number.isRequired,
  themeColor: PropTypes.string.isRequired,
};

const UserRecipesList = ({
  recipes,
  loading,
  recipesLimit,
  tabs,
  activeTab,
  onTabChange,
  onLimitChange,
  title, // Optional override for header text
}) => {
  const themeColor = getComputedStyle(document.body)
    .getPropertyValue("--bs-theme")
    .trim();

  const containerStyle = {
    height: "400px",
    overflowY: "auto",
  };

  const activeStyle = {
    backgroundColor: themeColor,
    border: `1px solid ${themeColor}`,
    color: "#fff",
    padding: "0.5rem 1rem",
    margin: "0.5rem",
    cursor: "pointer",
    borderRadius: "0.25rem",
  };
  const inactiveStyle = {
    backgroundColor: "transparent",
    border: `1px solid ${themeColor}`,
    color: themeColor,
    padding: "0.5rem 1rem",
    margin: "0.5rem",
    cursor: "pointer",
    borderRadius: "0.25rem",
  };

  let headerText = title;
  if (!headerText) {
    switch (activeTab) {
      case "users":
        headerText = "Latest User Recipes";
        break;
      case "brand":
        headerText = "Latest Brand Recipes";
        break;
      case "curated":
        headerText = "Latest Curated Recipes";
        break;
      case "all":
        headerText = "All Latest Recipes";
        break;
      default:
        headerText = "Recipes";
    }
  }

  return (
    <Card>
      <CardBody>
        {/* Header */}
        <div className="d-flex fw-bold small mb-3">
          <span className="flex-grow-1" style={{ fontSize: 15 }}>
            {headerText.toUpperCase()}
          </span>
        </div>

        {/* Tabs & Limit (theme-aware, consistent spacing) */}
        {tabs && tabs.length > 0 && (() => {
          const isThemeBlack = document.body.classList.contains('theme-black');
          const selected = (id) => (activeTab || (tabs[0]?.id ?? '')) === id;

          if (isThemeBlack) {
            // --- BLACK THEME: same button look you use elsewhere ---
            return (
              <div className="d-flex flex-wrap justify-content-center align-items-center mb-3 gap-2 theme-black-controls controls-row user-recipes-controls">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`btn freq-btn${selected(tab.id) ? ' active is-active' : ''}`}
                    aria-pressed={selected(tab.id)}
                    onClick={() => onTabChange(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}

                {onLimitChange && (
                  <input
                    type="text"
                    value={recipesLimit}
                    onChange={(e) => onLimitChange(e.target.value)}
                    placeholder="Limit"
                    className="form-control limit-input"
                  />
                )}
              </div>
            );
          }

          // --- NON-BLACK THEMES: nav-pills + gap like other components ---
          return (
            <div className="d-flex flex-wrap justify-content-center align-items-center mb-3 gap-2 user-recipes-controls">
              <ul className="nav nav-pills gap-2">
                {tabs.map((tab) => (
                  <li className="nav-item" key={tab.id}>
                    <button
                      type="button"
                      className={`nav-link ${selected(tab.id) ? 'active' : ''}`}
                      onClick={() => onTabChange(tab.id)}
                    >
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>

              {onLimitChange && (
                <input
                  type="text"
                  value={recipesLimit}
                  onChange={(e) => onLimitChange(e.target.value)}
                  placeholder="Limit"
                  className="form-control limit-input"
                />
              )}
            </div>
          );
        })()}


        {/* Content */}
        {loading ? (
          <div
            style={containerStyle}
            className="d-flex justify-content-center align-items-center"
          >
            <div
              className="spinner-border"
              role="status"
              style={{
                color: themeColor,
                width: "2rem",
                height: "2rem",
                borderWidth: "0.2em",
              }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : recipes && recipes.length > 0 ? (
          <div style={containerStyle}>
            <table className="table table-bordered">
              <tbody>
                {recipes.map((recipe, index) => (
                  <RecipeRow
                    key={recipe.id || index}
                    recipe={recipe}
                    rank={index + 1}
                    themeColor={themeColor}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            style={containerStyle}
            className="d-flex justify-content-center align-items-center"
          >
            No recipes found.
          </div>
        )}
      </CardBody>
    </Card>
  );
};

UserRecipesList.propTypes = {
  recipes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
      username: PropTypes.string,
      type: PropTypes.string,
      baseList: PropTypes.string,
      mixerList: PropTypes.string,
      garnishList: PropTypes.string,
      ingredients: PropTypes.string,
      custom_recipe_ingredient_base: PropTypes.array,
      custom_recipe_ingredient_garnish: PropTypes.array,
      custom_recipe_ingredient_mixer: PropTypes.array,
      image: PropTypes.string,
    })
  ).isRequired,
  loading: PropTypes.bool,
  recipesLimit: PropTypes.string,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  onLimitChange: PropTypes.func,
  title: PropTypes.string,
};

UserRecipesList.defaultProps = {
  loading: false,
  recipesLimit: "10",
  tabs: [
    { id: "users",   label: "Users" },
    { id: "brand",   label: "Brand" },
    { id: "curated", label: "Curated Recipes" },
    { id: "all",     label: "All Recipes" },
  ],
  activeTab: "users",
  onTabChange: () => {},
  onLimitChange: () => {},
  title: "",
};

export default UserRecipesList;
